import { RotateCcw, Sparkles, X } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import CertificationSelectPopover from "@/components/Shared/CertificationSelectPopover";
import CertificationChip from "@/components/Shared/TagChip/CertificationChip";
import TagChip from "@/components/Shared/TagChip/TagChip";
import TagSelectPopover from "@/components/Shared/TagSelectPopover";
import Organisation from "@/models/Organisation";
import CompetencePathPopover from "./CompetencePathPopover";
import {
	CompetenceFilter,
	CredentialFilter,
	competenceFiltersEqual,
	credentialFiltersEqual,
	isEmptyCredentialFilter,
} from "./jobConsultantPanel.utils";

export interface MatchingFilterBarProps {
	organisation?: Organisation;
	competenceFilter?: CompetenceFilter;
	onCompetenceFilterChange: React.Dispatch<React.SetStateAction<CompetenceFilter | undefined>>;
	credentialFilter: CredentialFilter;
	onCredentialFilterChange: React.Dispatch<React.SetStateAction<CredentialFilter>>;
	/** The job's own matching gate — the reset target and "seeded" indicator. */
	jobCompetenceFilter?: CompetenceFilter;
	jobRequirementsFilter: CredentialFilter;
}

/** Removable constraint pill of the competence filter (service / skill). */
const FilterPill: React.FC<{
	children: React.ReactNode;
	onRemove?: () => void;
	title?: string;
}> = ({ children, onRemove, title }) => (
	<span
		title={title}
		className="inline-flex max-w-full items-center gap-1 truncate rounded-[2px] border border-solid border-border-strong bg-white px-2 py-0.5 type-label font-medium leading-5 text-gray-900"
	>
		<span className="truncate">{children}</span>
		{onRemove && (
			<button
				type="button"
				onClick={onRemove}
				title="Ta bort"
				className="flex h-4 w-4 shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-current opacity-60 hover:bg-black/10 hover:opacity-100"
			>
				<X size={12} />
			</button>
		)}
	</span>
);

/**
 * The editable matching filter, two condensed rows: the active constraints
 * (competence path + skill pills + credential chips) on top, the
 * add-certification/tag actions below — both seeded from the job. Relaxing it
 * widens the match; one click resets back to the job's requirements.
 *
 * State lives in the parent — this component only renders and edits it.
 * Shared between {@link JobConsultantPanel} and the Kandidater tab.
 */
const MatchingFilterBar: React.FC<MatchingFilterBarProps> = ({
	organisation,
	competenceFilter,
	onCompetenceFilterChange,
	credentialFilter,
	onCredentialFilterChange,
	jobCompetenceFilter,
	jobRequirementsFilter,
}) => {
	const { t } = useTranslation();

	const certificationOptions = (organisation?.certificationTypes ?? []).map((type) => ({
		key: type.key,
		name: type.name.sv,
	}));
	const tagOptions = (organisation?.consultantTagTypes ?? []).map((type) => ({
		key: type.key,
		name: type.name.sv,
		color: type.color,
	}));

	// Competence labels from the org tree — the UNFILTERED tree, so archived
	// names still render (the popover filters archived entries for picking).
	const competenceTree = organisation?.competences ?? [];
	const branchItem = competenceTree.find((item) => item.key === competenceFilter?.branch);
	const serviceItem = branchItem?.items?.find((item) => item.key === competenceFilter?.service);
	const serviceLabel = [
		branchItem?.name.sv ?? competenceFilter?.branch,
		serviceItem?.name.sv ?? competenceFilter?.service,
	]
		.filter(Boolean)
		.join(" – ");
	const skillLabel = (key: string) =>
		serviceItem?.items?.find((item) => item.key === key)?.name.sv ?? key;

	// One reset restores the job's whole matching: competence AND credentials
	const filterMatchesJob =
		credentialFiltersEqual(credentialFilter, jobRequirementsFilter) &&
		competenceFiltersEqual(competenceFilter, jobCompetenceFilter);

	function resetToJobMatching() {
		onCredentialFilterChange(jobRequirementsFilter);
		onCompetenceFilterChange(jobCompetenceFilter);
	}

	function toggleSkill(key: string) {
		onCompetenceFilterChange((prev) => {
			if (!prev) return prev;
			const selected = prev.skills.includes(key);
			return {
				...prev,
				skills: selected ? prev.skills.filter((skill) => skill !== key) : [...prev.skills, key],
			};
		});
	}

	return (
		<div className="flex flex-col gap-1.5">
			{/* Row 1: the active constraints — competence path + credential chips */}
			<div className="flex flex-wrap items-center gap-1.5">
				<CompetencePathPopover
					competences={competenceTree}
					value={competenceFilter}
					onChange={onCompetenceFilterChange}
					jobCompetence={jobCompetenceFilter}
					triggerLabel={
						competenceFilter ? serviceLabel : (t("consultantPanel.allCompetences") as string)
					}
				/>
				{competenceFilter?.skills.map((key) => (
					<FilterPill
						key={key}
						title={t("consultantPanel.removeSkillHint") as string}
						onRemove={() => toggleSkill(key)}
					>
						{skillLabel(key)}
					</FilterPill>
				))}
				{credentialFilter.certifications.map((typeKey) => (
					<CertificationChip
						key={`certification:${typeKey}`}
						size="sm"
						name={certificationOptions.find((option) => option.key === typeKey)?.name ?? typeKey}
						onRemove={() =>
							onCredentialFilterChange((prev) => ({
								...prev,
								certifications: prev.certifications.filter((key) => key !== typeKey),
							}))
						}
					/>
				))}
				{credentialFilter.tags.map((key) => (
					<TagChip
						key={`tag:${key}`}
						size="sm"
						showIcon
						name={tagOptions.find((option) => option.key === key)?.name ?? key}
						color={tagOptions.find((option) => option.key === key)?.color}
						onRemove={() =>
							onCredentialFilterChange((prev) => ({
								...prev,
								tags: prev.tags.filter((tagKey) => tagKey !== key),
							}))
						}
					/>
				))}
			</div>

			{/* Row 2: the add-constraint actions, with the seed hint / reset at the edge */}
			<div className="flex flex-wrap items-center gap-1.5">
				{certificationOptions.length > 0 && (
					<CertificationSelectPopover
						options={certificationOptions}
						selectedKeys={credentialFilter.certifications}
						onSelect={(key) =>
							onCredentialFilterChange((prev) => ({
								...prev,
								certifications: [...prev.certifications, key],
							}))
						}
						onDeselect={(key) =>
							onCredentialFilterChange((prev) => ({
								...prev,
								certifications: prev.certifications.filter((k) => k !== key),
							}))
						}
						triggerLabel={t("consultantPanel.addCertification") as string}
						/* Same height as the chips in the row above */
						triggerClassName="px-2 py-0.5 type-label leading-5 border-border-strong"
						triggerIconSize={12}
					/>
				)}
				{tagOptions.length > 0 && (
					<TagSelectPopover
						options={tagOptions}
						selectedKeys={credentialFilter.tags}
						onSelect={(key) =>
							onCredentialFilterChange((prev) => ({ ...prev, tags: [...prev.tags, key] }))
						}
						onDeselect={(key) =>
							onCredentialFilterChange((prev) => ({
								...prev,
								tags: prev.tags.filter((tagKey) => tagKey !== key),
							}))
						}
						heading="Konsulttaggar"
						triggerLabel={t("consultantPanel.addTag") as string}
						/* Same height as the chips in the row above */
						triggerClassName="px-2 py-0.5 type-label leading-5 border-border-strong"
						triggerIconSize={12}
					/>
				)}
				{filterMatchesJob ? (
					(competenceFilter || !isEmptyCredentialFilter(credentialFilter)) && (
						<span
							className="ml-auto flex items-center gap-1 type-label text-muted-foreground"
							title={t("consultantPanel.jobRequirementsHint") as string}
						>
							<Sparkles size={12} className="shrink-0 opacity-70" />
							{t("consultantPanel.jobRequirements")}
						</span>
					)
				) : (
					<button
						type="button"
						onClick={resetToJobMatching}
						className="ml-auto flex items-center gap-1 border-0 bg-transparent p-0 type-label font-medium text-brand hover:underline"
					>
						<RotateCcw size={12} className="shrink-0" />
						{t("consultantPanel.resetRequirements")}
					</button>
				)}
			</div>
		</div>
	);
};

export default MatchingFilterBar;
